import FilterLines from "@redotech/redo-icons/filter-lines.svg";
import TrashIcon from "@redotech/redo-icons/trash-01.svg";
import XIcon from "@redotech/redo-icons/x-close.svg";
import { isPropertyNameValid } from "@redotech/redo-model/external-apis/custom-events/create-custom-event";
import {
  TimeframeConditionType,
  TimeframeUnit,
} from "@redotech/redo-model/marketing/segments/segment-timeframe";
import {
  ComparisonType,
  NumericCompareOperator,
  StringCompareOperators,
  WhereConditionDataType,
} from "@redotech/redo-model/segments/segment-where-condition";
import { assertNever } from "@redotech/util/type";
import { memo, useContext, useMemo, useState } from "react";
import { RedoButton } from "../../../arbiter-components/buttons/redo-button";
import { RedoTextInput } from "../../../arbiter-components/input/redo-text-input";
import { RedoListItem } from "../../../arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "../../../arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex, FlexProps } from "../../../flex";
import { ActivityCountSelector } from "../activities/segment-activity-count-selector";
import { BooleanFilter } from "../data-filters/boolean-filter";
import { FullDateTimeframeFilter } from "../data-filters/full-date-timeframe-filter";
import { NumericFilter } from "../data-filters/numeric-filter";
import { StringFilter } from "../data-filters/string-filter";
import {
  blockBorderProps,
  SegmentBuilderLocationContext,
  SegmentEditorLocation,
} from "../segment-builder-editor";
import { SegmentBuilderText } from "../segment-builder-text";
import {
  CustomEventConditionInProgress,
  StringConditionInProgress,
  WhereConditionInProgress,
} from "../segment-in-progress";
import { CustomEventNamesContext } from "./custom-event-names-context";
import {
  CustomEventPropertyKeysContext,
  PropertyInfo,
} from "./custom-event-property-keys-context";

function defaultPropertyFilter(): StringConditionInProgress<string> {
  return {
    type: WhereConditionDataType.STRING,
    dimension: "",
    comparison: {
      type: ComparisonType.STRING,
      operator: StringCompareOperators.CONTAINS,
      value: undefined,
    },
  };
}

function defaultFilterForProperty(
  property: PropertyInfo,
): WhereConditionInProgress<string> {
  const { key, type } = property;
  switch (type) {
    case "number":
      return {
        type: WhereConditionDataType.NUMERIC,
        dimension: key,
        comparison: {
          type: ComparisonType.NUMERIC,
          operator: NumericCompareOperator.EQ,
          value: undefined,
        },
      };
    case "boolean":
      return {
        type: WhereConditionDataType.BOOLEAN,
        dimension: key,
        comparison: { type: ComparisonType.BOOLEAN, value: true },
      };
    case "date":
      return {
        type: WhereConditionDataType.DATE_FULL,
        dimension: key,
        comparison: {
          type: TimeframeConditionType.BEFORE_NOW_RELATIVE,
          units: TimeframeUnit.DAY,
          value: 30,
        },
      };
    default:
      return {
        type: WhereConditionDataType.STRING,
        dimension: key,
        comparison: {
          type: ComparisonType.STRING,
          operator: StringCompareOperators.CONTAINS,
          value: undefined,
        },
      };
  }
}

export const SegmentCustomEventCondition = memo(
  function SegmentCustomEventCondition({
    condition,
    setCondition,
    deleteRequested,
  }: {
    condition: CustomEventConditionInProgress;
    setCondition: (condition: CustomEventConditionInProgress) => void;
    deleteRequested: () => void;
  }) {
    const location = useContext(SegmentBuilderLocationContext);

    const containerProps = useMemo<FlexProps>(() => {
      switch (location) {
        case SegmentEditorLocation.SEGMENT_BUILDER:
          return { ...blockBorderProps, p: "md" };
        case SegmentEditorLocation.AUTOMATIONS_PANEL:
          return {};
        default:
          assertNever(location);
      }
    }, [location]);

    return condition.eventName ? (
      <SegmentCustomEventConditionConfig
        condition={condition}
        containerProps={containerProps}
        deleteRequested={deleteRequested}
        setCondition={setCondition}
      />
    ) : (
      <Flex {...containerProps} align="center" justify="space-between">
        <SegmentCustomEventConditionInit
          deleteRequested={deleteRequested}
          eventNameSelected={(eventName) =>
            setCondition({ ...condition, eventName })
          }
        />
      </Flex>
    );
  },
);

const SegmentCustomEventConditionInit = memo(
  function SegmentCustomEventConditionInit({
    eventNameSelected,
    deleteRequested,
  }: {
    eventNameSelected: (name: string) => void;
    deleteRequested: () => void;
  }) {
    const eventNames = useContext(CustomEventNamesContext);

    const options: RedoListItem<string>[] = eventNames.map((name) => ({
      id: name,
      type: "text",
      text: name,
      value: name,
    }));

    return (
      <>
        <Flex align="center" gap="md">
          <SegmentBuilderText>Person has performed</SegmentBuilderText>
          {options.length > 0 ? (
            <RedoSingleSelectDropdownInput<string>
              fitToAnchor={false}
              options={options}
              optionSelected={({ item }) => eventNameSelected(item.value)}
              placeholder="Select event"
              size="xs"
            />
          ) : (
            <EventNameTextInput onConfirm={eventNameSelected} />
          )}
        </Flex>
        <Flex gap="xxs">
          <RedoButton
            IconLeading={TrashIcon}
            onClick={deleteRequested}
            theme="destructive"
          />
        </Flex>
      </>
    );
  },
);

const EventNameTextInput = memo(function EventNameTextInput({
  onConfirm,
}: {
  onConfirm: (name: string) => void;
}) {
  const [value, setValue] = useState("");

  return (
    <Flex align="center" gap="sm">
      <RedoTextInput
        placeholder="Event name"
        setValue={setValue}
        size="sm"
        value={value}
      />
      <RedoButton
        disabled={!isPropertyNameValid(value)}
        hierarchy="secondary"
        onClick={() => {
          if (isPropertyNameValid(value)) {
            onConfirm(value);
          }
        }}
        text="Use"
      />
    </Flex>
  );
});

const SegmentCustomEventConditionConfig = memo(
  function SegmentCustomEventConditionConfig({
    condition,
    setCondition,
    deleteRequested,
    containerProps,
  }: {
    condition: CustomEventConditionInProgress;
    setCondition: (condition: CustomEventConditionInProgress) => void;
    deleteRequested: () => void;
    containerProps: FlexProps;
  }) {
    const eventNames = useContext(CustomEventNamesContext);
    const propertyKeysMap = useContext(CustomEventPropertyKeysContext);
    const options: RedoListItem<string>[] = eventNames.map((name) => ({
      id: name,
      type: "text",
      text: name,
      value: name,
    }));
    const propertyKeys: PropertyInfo[] = condition.eventName
      ? (propertyKeysMap.get(condition.eventName) ?? [])
      : [];

    return (
      <Flex {...containerProps} dir="row" justify="space-between">
        <Flex dir="column" overflow="hidden">
          <Flex align="center" gap="md" wrap="wrap">
            <SegmentBuilderText>Person has performed</SegmentBuilderText>
            {options.length > 0 ? (
              <RedoSingleSelectDropdownInput<string>
                fitToAnchor={false}
                options={options}
                optionSelected={({ item }) =>
                  setCondition({ ...condition, eventName: item.value })
                }
                selectedItem={options.find((o) => o.id === condition.eventName)}
                size="xs"
              />
            ) : (
              <SegmentBuilderText>{condition.eventName!}</SegmentBuilderText>
            )}
            <ActivityCountSelector
              activityCount={condition.count}
              setActivityCount={(count) =>
                setCondition({ ...condition, count })
              }
            />
            <FullDateTimeframeFilter
              comparison={condition.timeframe}
              setComparison={(timeframe) =>
                setCondition({ ...condition, timeframe })
              }
            />
            <RedoButton
              IconLeading={FilterLines}
              onClick={() => {
                setCondition({
                  ...condition,
                  propertyFilters: [
                    ...condition.propertyFilters,
                    defaultPropertyFilter(),
                  ],
                });
              }}
            />
          </Flex>
          <CustomEventPropertyFilters
            propertyFilters={condition.propertyFilters}
            propertyKeys={propertyKeys}
            setPropertyFilters={(propertyFilters) =>
              setCondition({ ...condition, propertyFilters })
            }
          />
        </Flex>
        <Flex gap="xxs">
          <RedoButton
            IconLeading={TrashIcon}
            onClick={deleteRequested}
            theme="destructive"
          />
        </Flex>
      </Flex>
    );
  },
);

const PropertyComparisonFilter = memo(function PropertyComparisonFilter({
  filter,
  setFilter,
}: {
  filter: WhereConditionInProgress<string>;
  setFilter: (filter: WhereConditionInProgress<string>) => void;
}) {
  switch (filter.type) {
    case WhereConditionDataType.STRING:
      return (
        <StringFilter
          comparison={filter.comparison}
          setComparison={(comparison) => setFilter({ ...filter, comparison })}
        />
      );
    case WhereConditionDataType.NUMERIC:
      return (
        <NumericFilter
          comparison={filter.comparison}
          setComparison={(comparison) => setFilter({ ...filter, comparison })}
        />
      );
    case WhereConditionDataType.BOOLEAN:
      return (
        <BooleanFilter
          comparison={filter.comparison}
          setComparison={(comparison) => setFilter({ ...filter, comparison })}
        />
      );
    case WhereConditionDataType.DATE_FULL:
      return (
        <FullDateTimeframeFilter
          comparison={filter.comparison}
          setComparison={(comparison) => setFilter({ ...filter, comparison })}
        />
      );
    default:
      return null;
  }
});

const CustomEventPropertyFilters = memo(function CustomEventPropertyFilters({
  propertyFilters,
  propertyKeys,
  setPropertyFilters,
}: {
  propertyFilters: WhereConditionInProgress<string>[];
  propertyKeys: PropertyInfo[];
  setPropertyFilters: (filters: WhereConditionInProgress<string>[]) => void;
}) {
  return (
    <>
      {propertyFilters.map((filter, index) => (
        <CustomEventPropertyFilter
          filter={filter}
          index={index}
          key={index}
          propertyKeys={propertyKeys}
          removeFilter={() => {
            setPropertyFilters(propertyFilters.filter((_, i) => i !== index));
          }}
          setFilter={(updated) => {
            setPropertyFilters(
              propertyFilters.map((f, i) => (i === index ? updated : f)),
            );
          }}
        />
      ))}
    </>
  );
});

const CustomEventPropertyFilter = memo(function CustomEventPropertyFilter({
  filter,
  index,
  propertyKeys,
  setFilter,
  removeFilter,
}: {
  filter: WhereConditionInProgress<string>;
  index: number;
  propertyKeys: PropertyInfo[];
  setFilter: (filter: WhereConditionInProgress<string>) => void;
  removeFilter: () => void;
}) {
  const label = index === 0 ? "where" : "and";

  const propertyNameError =
    !propertyKeys.length &&
    filter.dimension.length > 0 &&
    !isPropertyNameValid(filter.dimension)
      ? "Only letters, numbers, spaces, _, - allowed"
      : undefined;

  const propertyOptions: RedoListItem<PropertyInfo>[] = propertyKeys.map(
    (p) => ({ id: p.key, type: "text", text: p.key, value: p }),
  );

  return (
    <Flex align="center" gap="md" pl="3xl" wrap="wrap">
      <SegmentBuilderText>{label}</SegmentBuilderText>
      {propertyOptions.length > 0 ? (
        <RedoSingleSelectDropdownInput<PropertyInfo>
          fitToAnchor={false}
          options={propertyOptions}
          optionSelected={({ item }) =>
            setFilter(defaultFilterForProperty(item.value))
          }
          placeholder="Property name"
          selectedItem={
            filter.dimension
              ? propertyOptions.find((o) => o.id === filter.dimension)
              : undefined
          }
          size="xs"
        />
      ) : (
        <RedoTextInput
          errorText={propertyNameError}
          placeholder="Property name"
          setValue={(value) => setFilter({ ...filter, dimension: value })}
          size="sm"
          value={filter.dimension}
        />
      )}
      <PropertyComparisonFilter filter={filter} setFilter={setFilter} />
      <RedoButton IconLeading={XIcon} onClick={removeFilter} />
    </Flex>
  );
});
